import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { setVoiceEnabled } from "../lib/speech";
import { setSfxEnabled, setMusicEnabled, startMusic, stopMusic, unlockAudio, hookMusicAutoStart } from "../lib/sounds";

const GameContext = createContext(null);
export const useGame = () => useContext(GameContext);

const ACTIVE_KEY = "speelslim_active_child";

function applyAccessibility(settings) {
  const root = document.documentElement;
  root.classList.toggle("dyslexia", !!settings?.dyslexia);
  root.classList.toggle("high-contrast", !!settings?.highContrast);
  root.classList.toggle("reduce-motion", !!settings?.reduceMotion);
}

function applyProfilePrefs(data) {
  applyAccessibility(data.settings);
  setVoiceEnabled(data.settings?.voice !== false);
  setSfxEnabled(data.settings?.sfx !== false);
  setMusicEnabled(data.settings?.music !== false);
}

export function GameProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/profiles/${id}`);
      setProfile(data);
      applyProfilePrefs(data);
      return data;
    } catch (e) {
      localStorage.removeItem(ACTIVE_KEY);
      setProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    hookMusicAutoStart();
    const id = localStorage.getItem(ACTIVE_KEY);
    if (id) loadProfile(id).finally(() => setLoading(false));
    else setLoading(false);
  }, [loadProfile]);

  const selectChild = async (id) => {
    localStorage.setItem(ACTIVE_KEY, id);
    return loadProfile(id);
  };

  const clearActive = () => {
    localStorage.removeItem(ACTIVE_KEY);
    setProfile(null);
    stopMusic();
  };

  const refresh = () => (profile ? loadProfile(profile.id) : null);

  const completeLesson = async (payload) => {
    if (!profile) return;
    const { data } = await api.post(`/profiles/${profile.id}/complete-lesson`, payload);
    setProfile(data);
    return data;
  };

  const addReward = async (delta) => {
    if (!profile) return;
    const { data } = await api.post(`/profiles/${profile.id}/reward`, delta);
    setProfile(data);
    return data;
  };

  const updatePet = async (patch) => {
    if (!profile) return;
    const { data } = await api.patch(`/profiles/${profile.id}/pet`, patch);
    setProfile(data);
    return data;
  };

  const petCare = async (action) => {
    if (!profile) return;
    const { data } = await api.post(`/profiles/${profile.id}/pet/care`, { action });
    setProfile(data);
    return data;
  };

  const updateSettings = async (patch) => {
    if (!profile) return;
    const next = { ...profile.settings, ...patch };
    setProfile((p) => ({ ...p, settings: next }));
    applyProfilePrefs({ settings: next });
    if (next.music !== false) startMusic(); else stopMusic();
    const { data } = await api.patch(`/profiles/${profile.id}`, { settings: next });
    setProfile(data);
    return data;
  };

  const enableAudio = () => {
    unlockAudio();
    if (profile?.settings?.music !== false) startMusic();
  };

  return (
    <GameContext.Provider value={{
      profile, loading, selectChild, clearActive,
      refresh, completeLesson, addReward, updatePet, petCare, updateSettings, enableAudio,
    }}>
      {children}
    </GameContext.Provider>
  );
}
